<template>
  <ModalDialog :show="state.visible" :title="`Exporter en ${state.format === 'pdf' ? 'PDF' : 'Excel'}`" :icon="state.format === 'pdf' ? 'fas fa-file-pdf' : 'fas fa-file-excel'" size="md" @update:show="(value) => !value && cancel()">
    <FormField v-slot="{ id }" label="Titre du document">
      <input :id="id" v-model.trim="state.title" class="form-control" maxlength="120" />
    </FormField>

    <div v-if="state.format === 'pdf'" class="mb-3">
      <span class="form-label d-block">Disposition de la page</span>
      <div class="orientation" role="radiogroup" aria-label="Orientation">
        <label :class="{ active: state.orientation === 'portrait' }">
          <input v-model="state.orientation" type="radio" value="portrait" class="visually-hidden" />
          <span class="sheet sheet--portrait" aria-hidden="true"></span>
          <span><i class="fas fa-file me-1" aria-hidden="true"></i>Portrait</span>
        </label>
        <label :class="{ active: state.orientation === 'landscape' }">
          <input v-model="state.orientation" type="radio" value="landscape" class="visually-hidden" />
          <span class="sheet sheet--landscape" aria-hidden="true"></span>
          <span><i class="fas fa-file fa-rotate-90 me-1" aria-hidden="true"></i>Paysage</span>
        </label>
      </div>
    </div>

    <div class="d-flex justify-content-between align-items-center mb-2">
      <span class="form-label mb-0">Colonnes à exporter <small class="text-muted fw-normal">({{ selectedCount }} / {{ state.columns.length }}) — glissez pour réordonner</small></span>
      <div class="d-flex gap-1">
        <button type="button" class="btn btn-ghost btn-sm" @click="setAll(true)"><i class="fas fa-check-double me-1" aria-hidden="true"></i>Tout</button>
        <button type="button" class="btn btn-ghost btn-sm" @click="setAll(false)"><i class="fas fa-eraser me-1" aria-hidden="true"></i>Aucune</button>
      </div>
    </div>

    <ul class="column-list" aria-label="Colonnes à exporter">
      <li
        v-for="(column, index) in state.columns"
        :key="column.key"
        :class="{ dragging: dragIndex === index, over: overIndex === index, off: !column.selected }"
        draggable="true"
        @dragstart="onDragStart(index, $event)"
        @dragover.prevent="overIndex = index"
        @dragleave="overIndex = null"
        @drop.prevent="onDrop(index)"
        @dragend="dragIndex = overIndex = null"
      >
        <i class="fas fa-grip-vertical grip" aria-hidden="true"></i>
        <span class="position">{{ index + 1 }}</span>
        <label class="form-check mb-0 flex-grow-1">
          <input v-model="column.selected" class="form-check-input" type="checkbox" />
          <span class="form-check-label">{{ column.label }}</span>
        </label>
        <button type="button" class="btn-icon" :disabled="index === 0" :aria-label="`Monter ${column.label}`" @click="move(index, -1)"><i class="fas fa-arrow-up" aria-hidden="true"></i></button>
        <button type="button" class="btn-icon" :disabled="index === state.columns.length - 1" :aria-label="`Descendre ${column.label}`" @click="move(index, 1)"><i class="fas fa-arrow-down" aria-hidden="true"></i></button>
      </li>
    </ul>

    <p v-if="!selectedCount" class="text-danger small mt-2 mb-0"><i class="fas fa-circle-exclamation me-1" aria-hidden="true"></i>Sélectionnez au moins une colonne.</p>

    <template #footer>
      <button type="button" class="btn btn-outline" @click="cancel"><i class="fas fa-xmark me-1" aria-hidden="true"></i>Annuler</button>
      <button type="button" class="btn btn-primary" :disabled="!selectedCount" @click="confirm">
        <i :class="state.format === 'pdf' ? 'fas fa-file-pdf' : 'fas fa-file-excel'" class="me-1" aria-hidden="true"></i>Exporter
      </button>
    </template>
  </ModalDialog>
</template>

<script setup>
import { computed, ref } from "vue";
import ModalDialog from "./ModalDialog.vue";
import FormField from "./FormField.vue";
import { exportDialogState as state, savePreferences } from "@/composables/useExportDialog.js";

const dragIndex = ref(null);
const overIndex = ref(null);

const selectedCount = computed(() => state.columns.filter((column) => column.selected).length);

const setAll = (value) => state.columns.forEach((column) => (column.selected = value));

function move(index, step) {
  const target = index + step;
  if (target < 0 || target >= state.columns.length) return;
  const [item] = state.columns.splice(index, 1);
  state.columns.splice(target, 0, item);
}

function onDragStart(index, event) {
  dragIndex.value = index;
  event.dataTransfer.effectAllowed = "move";
  event.dataTransfer.setData("text/plain", String(index));
}

function onDrop(index) {
  if (dragIndex.value === null || dragIndex.value === index) return;
  const [item] = state.columns.splice(dragIndex.value, 1);
  state.columns.splice(index, 0, item);
  dragIndex.value = overIndex.value = null;
}

function finish(result) {
  state.visible = false;
  const resolve = state.resolve;
  state.resolve = null;
  resolve?.(result);
}

function confirm() {
  if (state.preferenceKey) savePreferences(state.preferenceKey, state.columns, state.orientation);
  finish({
    columns: state.columns
      .filter((column) => column.selected)
      .map((column) => {
        const copy = { ...column };
        delete copy.selected;
        return copy;
      }),
    orientation: state.orientation,
    title: state.title,
  });
}

const cancel = () => finish(null);
</script>

<style scoped>
.orientation {
  display: flex;
  gap: 0.75rem;
}
.orientation label {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  border: 1px solid hsl(var(--border));
  border-radius: var(--radius);
  cursor: pointer;
  font-size: 0.875rem;
}
.orientation label.active {
  border-color: hsl(var(--primary));
  box-shadow: 0 0 0 3px hsl(var(--primary) / 0.15);
}
.sheet {
  border: 1.5px solid hsl(var(--muted-foreground));
  border-radius: 2px;
  background: hsl(var(--background));
}
.sheet--portrait { width: 18px; height: 24px; }
.sheet--landscape { width: 24px; height: 18px; }
.active .sheet { border-color: hsl(var(--primary)); background: hsl(var(--primary) / 0.12); }
.column-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 340px;
  overflow-y: auto;
  border: 1px solid hsl(var(--border));
  border-radius: var(--radius);
}
.column-list li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.6rem;
  border-bottom: 1px solid hsl(var(--border));
  background: hsl(var(--card));
  font-size: 0.875rem;
}
.column-list li:last-child {
  border-bottom: 0;
}
.column-list li.off .form-check-label {
  color: hsl(var(--muted-foreground));
  text-decoration: line-through;
}
.column-list li.dragging {
  opacity: 0.4;
}
.column-list li.over {
  box-shadow: inset 0 2px 0 hsl(var(--primary));
}
.grip {
  cursor: grab;
  color: hsl(var(--muted-foreground));
}
.position {
  width: 1.4rem;
  text-align: center;
  font-size: 0.72rem;
  color: hsl(var(--muted-foreground));
  font-variant-numeric: tabular-nums;
}
</style>
